import { createContext, useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  consumerClientContext,
  getClientLinks,
  MUTATE_REMOVE_CART,
  QUERY_GET_FILTERED_PARTNER,
  QUERY_GET_PARTNER,
  QUERY_GET_PARTNER_AND_CUSTOMER,
  QUERY_GET_STORE_CATEGORIES,
  useShopClient
} from 'shop/client'
import useHotjar from 'shop/hooks/useHotjar'
import { ApolloClient } from '@apollo/client'
import {
  Partner,
  PartnerStoreBrowse,
  FilteredPartnerParams
} from 'shop/components/Landing'
import { PartnerStore } from 'shop/components'
import config from 'shop/config'
import {
  CustomerDetails,
  ShopConfig,
  CategoriesResponse,
  GetCategoriesVariables
} from 'shop/types'
import {
  initializeMerchantGA4,
  initializeTagManager,
  initializePixel,
  initializeSlerpGA4,
  initializeGoogleConversion
} from 'tracker'
import { useRwg } from '../useRwg'
import { CategoryWithProducts } from 'shop/components/Shop/Categories'
import { format } from 'date-fns'
import { useQueryParamsFulfillment } from '../useQueryParamsFulfillment'
import { useQueryParams } from '../useQueryParams'
import AllTrackers from 'tracker/AllTrackers'
import { useFeatureValue, useGrowthBook } from '@growthbook/growthbook-react'

/**
 *  The shop context interface
 */
export type ShopInterface = {
  config: ShopConfig
  partner: Partner | null
  setPartner: React.Dispatch<Partner | null>
  customerId: string
  setCustomerId: React.Dispatch<any>
  isStoreLoading: boolean
  setIsStoreLoading: React.Dispatch<any>
  isProductsLoading: boolean
  setIsProductsLoading: React.Dispatch<any>
  useShopClient(): ApolloClient<object>
  customerApiKey: string
  setCustomerApiKey: React.Dispatch<any>
  customerDetails: CustomerDetails | null
  setCustomerDetails: React.Dispatch<
    React.SetStateAction<CustomerDetails | null>
  >
  isPartnerLoading: boolean
  setIsPartnerLoading: React.Dispatch<boolean>
  fetchPartnerAndCustomer: (
    args?: { date?: string; time?: string },
    forceFetch?: boolean
  ) => void
  setCurrentPartnerStore: React.Dispatch<PartnerStore>
  currentPartnerStore: PartnerStore | null
  categories: CategoryWithProducts[]
  setCategories: React.Dispatch<CategoryWithProducts[]>
  isLocationServiceEnabled: boolean
  setIsLocationServiceEnabled: React.Dispatch<boolean>
  isSortedPartnerStoresLoading: boolean
  setIsSortedPartnerStoresLoading: React.Dispatch<boolean>
  fetchSortedPartnerStores: (
    args: FilteredPartnerParams
  ) => Promise<PartnerStoreBrowse[]>
  fetchCategories: (
    getCategoryVars: GetCategoriesVariables
  ) => Promise<{ categories: CategoryWithProducts[] }>
}

const initialState = {
  appContent: null,
  config,
  useShopClient,
  partner: null,
  setPartner: () => {},
  customerId: '',
  setCustomerId: () => {},
  isStoreLoading: false,
  setIsStoreLoading: () => {},
  isProductsLoading: false,
  setIsProductsLoading: () => {},
  customerApiKey: '',
  setCustomerApiKey: () => {},
  customerDetails: null,
  setCustomerDetails: () => {},
  isPartnerLoading: true,
  setIsPartnerLoading: () => {},
  fetchPartnerAndCustomer: () => {},
  setCurrentPartnerStore: () => {},
  currentPartnerStore: null,
  categories: [],
  setCategories: () => {},
  isLocationServiceEnabled: false,
  setIsLocationServiceEnabled: () => {},
  isSortedPartnerStoresLoading: false,
  setIsSortedPartnerStoresLoading: () => {},
  fetchSortedPartnerStores: () => Promise.resolve([]),
  fetchCategories: (getCategoryVars: GetCategoriesVariables) =>
    new Promise<{ categories: CategoryWithProducts[] }>(() => {})
}

export const SetupSlerpShop = (searchQueryParams?: URLSearchParams) => {
  const client = useShopClient()
  const { getQueryParam, deleteQueryParam } = useQueryParams()
  const location = useLocation()
  const [partner, setPartner] = useState<Partner | null>(null)
  const [isPartnerLoading, setIsPartnerLoading] = useState<boolean>(true)
  const [customerId, setCustomerId] = useState(
    localStorage.getItem('customerId') || ''
  )
  const [customerApiKey, setCustomerApiKey] = useState(
    localStorage.getItem('customerApiKey') || ''
  )
  const [isStoreLoading, setIsStoreLoading] = useState(true)
  const [isProductsLoading, setIsProductsLoading] = useState(true)
  const [customerDetails, setCustomerDetails] =
    useState<CustomerDetails | null>(null)
  const [hasRecentlyFetchedPartner, setHasRecentlyFetchedPartner] =
    useState(false)
  const [currentPartnerStore, setCurrentPartnerStore] =
    useState<PartnerStore | null>(null)
  const [categories, setCategories] = useState<CategoryWithProducts[]>([])
  const [isLocationServiceEnabled, setIsLocationServiceEnabled] =
    useState(false)
  const [isSortedPartnerStoresLoading, setIsSortedPartnerStoresLoading] =
    useState(false)
  const [isCartRemovalInProgress, setIsCartRemovalInProgress] = useState(false)

  const { setRWGOnEnrichedEventData } = useRwg(
    config.domain,
    getQueryParam,
    deleteQueryParam
  )
  const { initHotjar, removeHotjar } = useHotjar()
  const { fulfillmentQueryParams } =
    useQueryParamsFulfillment(searchQueryParams)

  const environment = process.env.REACT_APP_TEST_ENV || process.env.NODE_ENV

  const growthbook = useGrowthBook()
  const [gbFeaturesLoaded, setGbFeaturesLoaded] = useState(false)
  const gbFeatureDefaultToBrowse = useFeatureValue<boolean>(
    'landing_page_default_to_browse',
    false
  )
  const navigate = useNavigate()

  /** Removes cart details from localStorage & mutation detaches the cart from the existing user/auth token */
  const handleCartRemoval = () => {
    // Only remove cart if we are on the landing page
    const isLandingRoute =
      location.pathname === '/' || location.pathname.startsWith('/browse')
    if (!isLandingRoute) return Promise.resolve()

    const cartDetails = localStorage.getItem(config.domain)
    if (!!cartDetails && !isCartRemovalInProgress) {
      setIsCartRemovalInProgress(true)
      localStorage.removeItem(config.domain)
      return client
        .mutate({
          mutation: MUTATE_REMOVE_CART,
          ...consumerClientContext
        })
        .finally(() => {
          setIsCartRemovalInProgress(false)
        })
    }
    return Promise.resolve()
  }

  /**
   * Fetch the Partner and Customer query and set it in the Context.
   * Has a debounce of 5 mins
   */
  const fetchPartnerAndCustomer = (
    args?: { date?: string; time?: string },
    forceFetch?: boolean
  ) => {
    if (hasRecentlyFetchedPartner && !forceFetch) return
    setHasRecentlyFetchedPartner(true)
    setIsPartnerLoading(true)

    const query = customerId
      ? QUERY_GET_PARTNER_AND_CUSTOMER
      : QUERY_GET_PARTNER

    client
      .query({
        query: query,
        variables: {
          fulfillmentDate: args?.date,
          fulfillmentTime: args?.time
        },
        ...consumerClientContext,
        fetchPolicy: 'network-only',
        errorPolicy: 'all'
      })
      .then(({ data }) => {
        const { analytics } = data.partner

        if (analytics.enabled) {
          /** Update Trackers with Merchant data from ShopConfig */
          const enrichedMerchantData = {
            merchant_id: config.partnerId,
            merchant_slug: config.domain
          }
          const { google, facebook } = analytics
          initializeMerchantGA4(
            google.analytics.id,
            customerId,
            enrichedMerchantData
          )
          initializeTagManager(google.tagManager.id)
          initializeGoogleConversion(google.ads.id, google.ads.conversionId)
          initializePixel(facebook.pixel.id)

          if (AllTrackers.getInstance().slerpGA4Tracker.isTrackingEnabled) {
            AllTrackers.getInstance().slerpGA4Tracker.setTrackingCustomerId(
              customerId
            )
          } else {
            initializeSlerpGA4(customerId, enrichedMerchantData)
          }

          setRWGOnEnrichedEventData() // has to be called once GA4 has been initialised
        }

        setPartner(data.partner)

        if (customerId) {
          setCustomerDetails(data.customer)
        }

        handleCartRemoval()
      })
      .finally(() => {
        setIsPartnerLoading(false)
      })

    const SECOND = 1000
    setTimeout(() => {
      setHasRecentlyFetchedPartner(false)
    }, 300 * SECOND) // 5 mins = 300 secs
  }

  const fetchSortedPartnerStores = async (
    filterArgs: FilteredPartnerParams
  ) => {
    setIsSortedPartnerStoresLoading(true)

    const query = QUERY_GET_FILTERED_PARTNER

    return client
      .query({
        query: query,
        ...consumerClientContext,
        variables: filterArgs,
        fetchPolicy: 'network-only',
        errorPolicy: 'all'
      })
      .then(({ data }) => {
        const stores = (data.partner?.stores as PartnerStoreBrowse[]) || []

        return handleCartRemoval()
          .then(() => stores)
          .catch((err) => {
            console.error(err)
            return stores
          })
      })
      .catch((err) => {
        console.error(err)
        return []
      })
      .finally(() => {
        setTimeout(() => setIsSortedPartnerStoresLoading(false), 100)
      })
  }

  useEffect(() => {
    /** START OF GROWTHBOOK TEST EXPERIMENT */
    // Wait for Growthbook to be ready, so that features are loaded.
    if (!gbFeaturesLoaded) return
    // On useShop init, check Growthbook experiment and navigate from Landing to Browse,
    // calling the original partner query in the background.
    if (location.pathname === '/' && !!gbFeatureDefaultToBrowse) {
      navigate('/browse', { replace: true })
    }
    /** END OF GROWTHBOOK TEST EXPERIMENT */

    // get partner data on load
    // only pass time arg if we have a time param
    const dateTimeArgs = fulfillmentQueryParams.fulfillmentDatetime && {
      date: format(fulfillmentQueryParams.fulfillmentDatetime, 'yyyy-MM-dd'),
      time: fulfillmentQueryParams.fulfillmentTime
        ? format(fulfillmentQueryParams.fulfillmentDatetime, 'HH:mm:ss')
        : undefined
    }

    fetchPartnerAndCustomer(dateTimeArgs, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gbFeaturesLoaded])

  useEffect(() => {
    // Wait for Growthbook to be ready, so that features are loaded.
    if (growthbook.ready) setGbFeaturesLoaded(true)
  }, [growthbook.ready])

  useEffect(() => {
    if (partner?.analytics.enabled) {
      initHotjar({ environment: environment })
    }

    return () => {
      // Cleanup Hotjar on unmount
      removeHotjar()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner])

  /** Update client with Partner data */
  useEffect(() => {
    // refresh client link with merchant id
    const { domain, apiKey } = config
    const clientLinks = getClientLinks(domain, apiKey, config.partnerId)
    client.setLink(clientLinks)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchCategories = (getCategoryVars: GetCategoriesVariables) => {
    setIsProductsLoading(true)
    return client
      .query<CategoriesResponse>({
        query: QUERY_GET_STORE_CATEGORIES,
        variables: getCategoryVars,
        fetchPolicy: 'network-only',
        ...consumerClientContext
      })
      .then(({ data }) => {
        return data.store
      })
  }

  return {
    config,
    useShopClient,
    partner,
    setPartner,
    customerId,
    setCustomerId,
    isStoreLoading,
    setIsStoreLoading,
    isProductsLoading,
    setIsProductsLoading,
    customerApiKey,
    setCustomerApiKey,
    customerDetails,
    setCustomerDetails,
    isPartnerLoading,
    setIsPartnerLoading,
    fetchPartnerAndCustomer,
    setCurrentPartnerStore,
    currentPartnerStore,
    categories,
    setCategories,
    isLocationServiceEnabled,
    setIsLocationServiceEnabled,
    isSortedPartnerStoresLoading,
    setIsSortedPartnerStoresLoading,
    fetchSortedPartnerStores,
    fetchCategories
  }
}

export const ShopContext = createContext<ShopInterface>(initialState)

export const useShop = () => {
  return useContext(ShopContext)
}
